import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { Link, useNavigate } from "react-router";
import Constant from "../../../utils/constant";
import { IContent } from "../../../model/interface/content";
import { useForm } from "react-hook-form";
import Breadcrumb from "../../shared/common/breadcrumb";
import { IBreadcrumb } from "../../../model/interface/breadcrumb";
import Loader from "../../shared/loader/loader";
import AllowedTo from "../../shared/common/rbac";
import Permissions from "../../../utils/permissions";
import AccessDenied from "../../shared/common/accessDenied";
import { useNumericParam } from "../../../utils/useNumericParam";

const ManageContent: React.FC<any> = (props: any) => {
  const [lang, setLang] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);
  const [contentToEdit, setContentToEdit] = useState<IContent>();
  const [contentIcon, setContentIcon] = useState<any>();
  const id = useNumericParam("id");

  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    const contentAreaId = id;
    if (contentAreaId === 0) {
      navigate(Constant.BAD_REQUEST, { replace: true });
    } else if (contentAreaId) {
      content();
    }
  }, [id]);

  async function content() {
    setLoading(true);
    const service = await import("../../../api/contentArea.service");
    const response = service.getContentAreaById(id);
    const data = (await response).data;
    setContentToEdit(data);
    setLoading(false);
  }

  const onSubmit = async (data) => {
    const service = await import("../../../api/contentArea.service");

    const formData = new FormData();
    formData.append("contentAreaFile", contentIcon);
    formData.append(
      "contentAreaId",
      (contentToEdit ? contentToEdit.contentAreaId : 0).toString()
    );
    formData.append("name", data.name);
    formData.append("abbreviation", data.abbreviation);
    formData.append("contentAreaIcon", data.contentAreaIcon);

    if (data?.spanishName) {
      formData.append("spanishName", data.spanishName);
    }
    setLoading(true);
    contentToEdit
      ? service.updateContentArea(formData).then(() => {
          navigate(Constant.CONTENTS);
          setLoading(false);
        })
      : service.postContentArea(formData).then(() => {
          navigate(Constant.CONTENTS);
          setLoading(false);
        });
  };

  const breadcrumbItems = () => {
    const items: Array<IBreadcrumb> = [
      { name: "Content Area", url: Constant.CONTENTS },
      {
        name: contentToEdit ? "Edit Content Area" : "Add Content Area",
        url: "",
      },
    ];

    if (id) {
      items.push({
        name: "",
        url: "",
        objectId: id?.toString(),
      });
    }

    return items;
  };

  const toggleLang = () => {
    if (lang === 1) {
      setLang(2);
    } else {
      setLang(1);
    }
  };

  const toInputUppercase = (e) => {
    e.target.value = ("" + e.target.value).toUpperCase();
  };

  async function inputChangeHandler(e: any) {
    const target = e.target;
    const value = target.value;
    const name = target.name;

    if (name === "icon" && e?.target?.files?.length) {
      setContentIcon(e.target.files[0]);
    }
  }

  return (
    <AllowedTo
      perform={[
        Permissions.content_area_create,
        Permissions.content_area_modify,
      ]}
      no={() => <AccessDenied />}
    >
      {loading && <Loader />}

      <div className="flex-1 overflow-auto">
        <div className=" h-auto min-h-half w-full rounded-bl-3xl bg-site-teal px-8  py-8 pt-0 pb-14 overflow-y-auto mt-7 ">
          <div className="relative  py-1">
            <div className="overlay-image-main pointer-events-none fixed left-0 top-0 w-screen h-screen z-20"></div>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-light text-gray-900">
                {contentToEdit ? "Edit Content Area " : "Add Content Area "}
              </h3>
            </div>
            <Breadcrumb items={breadcrumbItems()} />
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="shadow border rounded-lg mt-4 w-1/4 px-4 py-6">
                <div className="flex justify-end">
                  <div
                    className="inline-block border text-sm border-gray-300 overflow-hidden rounded-md mb-4"
                    onClick={() => toggleLang()}
                  >
                    <span
                      className={`${
                        lang === 1
                          ? "bg-primary-color text-white"
                          : "bg-white text-gray-400"
                      } inline-block dark:text-white w-20 py-0.5 text-center cursor-pointer hover:opacity-80`}
                    >
                      English
                    </span>
                    <span
                      className={`${
                        lang === 2
                          ? "bg-primary-color text-white"
                          : "bg-white text-gray-400"
                      } w-20 py-0.5 inline-block text-center  cursor-pointer hover:text-gray-500 dark:hover:text-gray-400`}
                    >
                      Spanish
                    </span>
                  </div>
                </div>
                <div>
                  <div className="grid grid-cols-1 gap-5">
                    <div className="text-sm col-span-12">
                      <label className="block font-medium text-gray-700 whitespace-nowrap">
                        Name
                        {lang === Constant.LANGUAGE.English && (
                          <span className="text-red-600">*</span>
                        )}
                      </label>

                      <input
                        type="text"
                        defaultValue={contentToEdit && contentToEdit.name}
                        autoComplete="off"
                        className={`${
                          lang === 1 ? "" : "hidden"
                        } mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm`}
                        {...register("name", {
                          required: true,
                          value: contentToEdit && contentToEdit.name,
                        })}
                      />

                      <input
                        type="text"
                        defaultValue={
                          contentToEdit && contentToEdit.spanishName
                        }
                        autoComplete="off"
                        className={`${
                          lang === 2 ? "" : "hidden"
                        } mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm`}
                        {...register("spanishName")}
                      />

                      {errors.name && (
                        <span className="text-red-700 inline-block pt-1 text-sm">
                          Name is required.
                        </span>
                      )}
                    </div>
                    <div className="text-sm col-span-12">
                      <label className="block font-medium text-gray-700 whitespace-nowrap">
                        Abbreviation
                        <span className="text-red-600 text-sm">*</span>
                      </label>
                      <input
                        type="text"
                        maxLength={2}
                        defaultValue={
                          contentToEdit &&
                          contentToEdit.abbreviation?.toUpperCase()
                        }
                        onInput={toInputUppercase}
                        autoComplete="off"
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm"
                        {...register("abbreviation", {
                          required: true,
                          maxLength: 2,
                          value:
                            contentToEdit &&
                            contentToEdit.abbreviation?.toUpperCase(),
                        })}
                      />
                      {errors.abbreviation &&
                        errors.abbreviation.type === "required" && (
                          <span className="pt-1 inline-block text-red-700 text-sm">
                            Abbreviation is required.
                          </span>
                        )}
                      {errors.abbreviation &&
                        errors.abbreviation.type === "maxLength" && (
                          <span className="text-red-700 text-sm inline-block pt-1">
                            Abbreviation must not be more than 2 characters.
                          </span>
                        )}
                    </div>

                    <div className="text-sm col-span-12">
                      <label className="block font-medium text-gray-700 whitespace-nowrap">
                        Upload Icon
                      </label>

                      <div className="flex items-center">
                        <input
                          type="hidden"
                          {...register("contentAreaIcon", {
                            value:
                              contentToEdit && contentToEdit.contentAreaIcon,
                          })}
                        ></input>
                        <input
                          type="file"
                          id="icon"
                          name="icon"
                          onChange={inputChangeHandler}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm"
                          defaultValue={contentToEdit?.contentAreaIcon}
                        />
                        {contentToEdit?.contentAreaIcon && (
                          <img
                            title={props.title}
                            className="icon ml-2 h-10"
                            src={contentToEdit?.contentAreaIcon}
                            alt="Image not found"
                          />
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end mt-8 items-center border-t ">
                    <button
                      type="submit"
                      className="inline-flex items-center px-3 py-2 my-3 hover:bg-secondary-color border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white dark:text-white bg-primary-color  focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color"
                    >
                      {contentToEdit ? "Update " : "Save"}
                    </button>
                    <Link to={Constant.CONTENTS} className="ml-2">
                      <button className="inline-flex items-center px-3 py-2 my-3 hover:text-gray-500 border border-gray-300 text-sm leading-4 font-medium rounded-md shadow-sm text-gray-700 bg-white  focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color">
                        Cancel
                      </button>
                    </Link>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </AllowedTo>
  );
};

const mapStateToProps = (state: any) => {
  return {
    profile: state.profile,
  };
};

export default connect(mapStateToProps, {})(ManageContent);
